import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import { type Filters } from "@/hooks/admin/AdminTodosTabs/useFilteredTasks";
import { type TaskTag } from "@/hooks/admin/AdminTodosTabs/useTasks";
import { type Database } from "@/integrations/supabase/types";
import { type Property } from "@/types/property";
import { translateRoleToSpanish } from "@/utils/shared/translateRoleToSpanish";

import { X } from "lucide-react";

type User = {
  created_at: string;
  email: string | null;
  first_name: string;
  id: string;
  invitation_expires_at: string | null;
  invitation_token: string | null;
  last_name: string;
  phone: string | null;
  role: Database["public"]["Enums"]["user_role"];
  status: Database["public"]["Enums"]["user_status"];
  updated_at: string;
  user_id: string;
};

interface TaskFiltersProps {
  clearFilters: () => void;
  filters: Filters;
  hasActiveFilters: boolean;
  properties: Property[];
  setFilters: (value: React.SetStateAction<Filters>) => void;
  tags: TaskTag[];
  users: User[];
}

const TaskFilters = ({
  filters,
  setFilters,
  clearFilters,
  hasActiveFilters,
  users,
  properties,
  tags,
}: TaskFiltersProps) => {
  const availableUsers = users.filter((u) => u.role !== "property_owner");

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          Filtros
          {hasActiveFilters && (
            <Button
              variant="ghost"
              size="sm"
              onClick={clearFilters}
              className="text-muted-foreground hover:text-foreground"
            >
              <X className="h-4 w-4 mr-1" />
              Limpiar filtros
            </Button>
          )}
        </CardTitle>
      </CardHeader>

      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              Usuario asignado
            </label>

            <Select
              value={filters.assignedUser}
              onValueChange={(value) =>
                setFilters((prev) => ({ ...prev, assignedUser: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Todos los usuarios" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="__all__">Todos los usuarios</SelectItem>
                {availableUsers.map((user) => (
                  <SelectItem
                    key={user.user_id}
                    value={user.user_id || `user-${user.id}`}
                  >
                    {user.first_name} {user.last_name}
                    {" - "}
                    <span className="text-muted-foreground text-xs">
                      {translateRoleToSpanish(user.role)}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Propiedad</label>

            <Select
              value={filters.property}
              onValueChange={(value) =>
                setFilters((prev) => ({ ...prev, property: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Todas las propiedades" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="__all__">Todas las propiedades</SelectItem>

                {properties.map((property: any) => (
                  <SelectItem key={property.id} value={property.id}>
                    {property.short_name || property.title}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">Etiqueta</label>

            <Select
              value={filters.tag}
              onValueChange={(value) =>
                setFilters((prev) => ({ ...prev, tag: value }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Todas las etiquetas" />
              </SelectTrigger>

              <SelectContent>
                <SelectItem value="__all__">Todas las etiquetas</SelectItem>

                {tags.map((tag) => (
                  <SelectItem key={tag.id} value={tag.id}>
                    <div className="flex items-center gap-2">
                      <div
                        className="w-3 h-3 rounded-full"
                        style={{ backgroundColor: tag.color }}
                      />
                      {tag.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default TaskFilters;
